// The right-hand live resource panel.
//
// On the `components/ui` primitives: the panel shell is a Card (padded={false}
// — the sections carry their own padding), the refresh affordance is a
// LinkButton, the loading and empty blocks are Spinner and EmptyState, and the
// per-instance status pill is a Badge.
//
// The blue/purple/orange meters are deliberately NOT remapped onto the six
// colour roles: they encode three distinct metrics (CPU / memory / disk), the
// way a chart's series colours do, not three semantic states.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { UserResourceStats, HostResourceStats } from '../../api/client'
import { Badge, Card, EmptyState, LinkButton, Spinner, cx } from '../../components/ui'

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

export function ResourceUsagePanel({ stats, loading, onRefresh, hostStats }: { stats: UserResourceStats[]; loading: boolean; onRefresh: () => void; hostStats?: HostResourceStats }) {
  const { t } = useTranslation()
  const [expandedUser, setExpandedUser] = useState<string | null>(null)

  const totalCPU = stats.reduce((s, u) => s + u.total_cpu_percent, 0)
  const totalMem = stats.reduce((s, u) => s + u.total_memory_usage, 0)
  const totalRunning = stats.reduce((s, u) => s + u.running_count, 0)
  const totalInstances = stats.reduce((s, u) => s + u.instance_count, 0)

  const hostMemPct = hostStats && hostStats.mem_total > 0
    ? (hostStats.mem_used / hostStats.mem_total) * 100
    : 0
  const hostDiskPct = hostStats && hostStats.disk_total > 0
    ? (hostStats.disk_used / hostStats.disk_total) * 100
    : 0

  return (
    <div className="w-80 shrink-0 hidden xl:block">
      <Card padded={false} className="sticky top-8">
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{t('users.resourceUsage')}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{t('users.resourceUsageHint')}</p>
            </div>
            <LinkButton
              tone="neutral"
              onClick={onRefresh}
              className="p-1.5 rounded-lg hover:bg-gray-100"
              title={t('users.refreshStats')}
              aria-label={t('users.refreshStats')}
            >
              <svg className={cx('w-4 h-4', loading && 'animate-spin')} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </LinkButton>
          </div>

          {/* Summary totals */}
          {!loading && stats.length > 0 && (
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="bg-blue-50 rounded-lg px-3 py-2">
                <div className="text-xs text-blue-600 font-medium">{t('users.cpuUsage')}</div>
                <div className="text-lg font-bold text-blue-700">{totalCPU.toFixed(1)}%</div>
              </div>
              <div className="bg-purple-50 rounded-lg px-3 py-2">
                <div className="text-xs text-purple-600 font-medium">{t('users.memoryUsage')}</div>
                <div className="text-lg font-bold text-purple-700">{formatBytes(totalMem)}</div>
              </div>
            </div>
          )}

          {!loading && stats.length > 0 && (
            <div className="flex gap-3 mt-3 text-xs text-gray-500">
              <span>{totalInstances} {t('users.instances')}</span>
              <span className="text-green-600">{totalRunning} {t('users.running')}</span>
            </div>
          )}
        </div>

        {/* Host stats section */}
        {hostStats && (
          <div className="px-5 py-4 border-b border-gray-100">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{t('users.hostStats')}</h4>
            <div className="space-y-2.5">
              {/* Host CPU */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-500">{t('users.hostCPU')}</span>
                  <span className="font-medium text-blue-700">{hostStats.cpu_percent.toFixed(1)}%</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-400 rounded-full transition-all"
                    style={{ width: `${Math.min(hostStats.cpu_percent, 100)}%` }}
                  />
                </div>
              </div>
              {/* Host Memory */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-500">{t('users.hostMemory')}</span>
                  <span className="font-medium text-purple-700">{formatBytes(hostStats.mem_used)} / {formatBytes(hostStats.mem_total)}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-purple-400 rounded-full transition-all"
                    style={{ width: `${Math.min(hostMemPct, 100)}%` }}
                  />
                </div>
              </div>
              {/* Host Disk */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-500">{t('users.hostDisk')}</span>
                  <span className="font-medium text-orange-700">{formatBytes(hostStats.disk_used)} / {formatBytes(hostStats.disk_total)}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-orange-400 rounded-full transition-all"
                    style={{ width: `${Math.min(hostDiskPct, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Per-user breakdown */}
        <div className="max-h-[calc(100vh-300px)] overflow-y-auto">
          {loading ? (
            <div className="px-5 py-8 flex flex-col items-center text-center gap-2">
              <Spinner className="w-5 h-5 text-brand-500" />
              <p className="text-xs text-gray-500">{t('users.loadingStats')}</p>
            </div>
          ) : stats.length === 0 ? (
            <EmptyState
              className="py-8"
              icon={
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              }
              title={t('users.noRunningInstances')}
            />
          ) : (
            <div className="divide-y divide-gray-100">
              {stats.map((us) => (
                <UserStatsRow
                  key={us.user_id}
                  stats={us}
                  expanded={expandedUser === us.user_id}
                  onToggle={() => setExpandedUser(expandedUser === us.user_id ? null : us.user_id)}
                />
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}

export function UserStatsRow({ stats: us, expanded, onToggle }: { stats: UserResourceStats; expanded: boolean; onToggle: () => void }) {
  const { t } = useTranslation()
  const memPct = us.instances.length > 0 && us.instances[0].memory_limit > 0
    ? (us.total_memory_usage / us.instances.reduce((s, i) => s + i.memory_limit, 0)) * 100
    : 0

  return (
    <div>
      {/* Not a Button: this is a full-width disclosure row, and Button's size
          scale (its padding) would be a lie here. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full px-5 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <div className="shrink-0 w-7 h-7 bg-gradient-to-br from-brand-100 to-brand-50 rounded-full flex items-center justify-center text-brand-600 text-xs font-semibold border border-brand-200">
          {us.user_email.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-900 truncate">{us.user_email}</p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-gray-400">{us.running_count}/{us.instance_count}</span>
            {/* CPU bar */}
            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 rounded-full transition-all"
                style={{ width: `${Math.min(us.total_cpu_percent, 100)}%` }}
              />
            </div>
            <span className="text-[10px] text-blue-600 font-medium w-10 text-right">{us.total_cpu_percent.toFixed(1)}%</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[10px] text-gray-400">{t('users.memoryUsage')}</span>
            {/* Memory bar */}
            <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-purple-500 rounded-full transition-all"
                style={{ width: `${Math.min(memPct, 100)}%` }}
              />
            </div>
            <span className="text-[10px] text-purple-600 font-medium w-10 text-right">{formatBytes(us.total_memory_usage)}</span>
          </div>
        </div>
        <svg className={cx('w-4 h-4 text-gray-400 transition-transform', expanded && 'rotate-180')} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="px-5 pb-3 space-y-2">
          {us.instances.map((inst) => {
            const instMemPct = inst.memory_limit > 0 ? (inst.memory_usage / inst.memory_limit) * 100 : 0
            return (
              <div key={inst.instance_id} className="bg-gray-50 rounded-lg px-3 py-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-700 truncate">{inst.instance_name}</span>
                  <Badge tone={inst.status === 'running' ? 'success' : 'neutral'}>{inst.status}</Badge>
                </div>
                {inst.status === 'running' && (
                  <div className="mt-1.5 space-y-1">
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className="text-gray-400 w-8">{t('users.cpuUsage')}</span>
                      <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-400 rounded-full" style={{ width: `${Math.min(inst.cpu_percent, 100)}%` }} />
                      </div>
                      <span className="text-blue-600 w-10 text-right">{inst.cpu_percent.toFixed(1)}%</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px]">
                      <span className="text-gray-400 w-8">{t('users.memShort')}</span>
                      <div className="flex-1 h-1 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-purple-400 rounded-full" style={{ width: `${Math.min(instMemPct, 100)}%` }} />
                      </div>
                      <span className="text-purple-600 w-10 text-right">{formatBytes(inst.memory_usage)}</span>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
